// src/features/reference-ncs/chercheur.ts — SCE Issues
// Recherche de FOND des N° de NC-S (06/10, demande de Guillaume) : chaque fois
// qu'une page charge une liste de problèmes, ceux qui portent le badge NC-S
// sans leur N° sont cherchés dans le registre qualité, et le N° s'écrit s'il
// est sûr. Rien n'attend cette recherche : la page est déjà affichée.
//
// ─── Ce que ça coûte ───
//
// Rien du tout s'il n'y a aucune NC-S en attente. Sinon : la petite requête
// de version du registre (~1 ko), la liste du dossier « Déclarations NC-S »
// (une requête, Titre = ID du problème), et une fois par fichier son
// formulaire (~100 ko, gardé). Le registre (10 Mo) n'est retéléchargé que
// s'il a changé, et lu dans un worker. Un problème n'est pas recherché plus
// d'une fois toutes les 10 minutes dans un onglet.
//
// Les cas douteux ne s'écrivent pas : ils attendent la fiche, qui les
// propose (hooks/useReferenceNcs).

import { addCommentaire } from '@/api/commentaires.api'
import { enregistrerReferenceNcs, getIssueById } from '@/api/issues.api'
import {
  RegistreInaccessible,
  avecVerrou,
  declarationEnvoyee,
  declarationsDeposees,
  inaccessibleAujourdhui,
  lignesRegistreNcs,
  noterInaccessible,
  type DeclarationDeposee,
} from '@/api/registreNcs.api'
import { chercherDansRegistre } from '@/utils/registreNcs'
import { enregistrerSuiviNcs } from '@/api/suiviNcs.api'
import type { BlocSuiviNcs } from '@/utils/suiviNcs'
import type { Issue } from '@/types/issue.types'

/** Un problème n'est pas recherché plus souvent, dans un même onglet. */
const INTERVALLE_MS = 10 * 60 * 1000

const tentatives = new Map<string, number>()
let enCours = false

/** Les problèmes qui attendent leur N° de NC-S. */
export function enAttenteDeNumero(issues: readonly Issue[]): Issue[] {
  return issues.filter((i) => i.badgesCloture?.includes('NC-S') && !i.refNcs && i.statut !== 'Annulé')
}

/** Jour local d'une date ISO. */
function jourDe(iso: string): string {
  const d = new Date(iso)
  const p = (n: number): string => String(n).padStart(2, '0')
  return Number.isNaN(d.getTime()) ? iso.slice(0, 10) : `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/**
 * Cherche et écrit les N° manquants parmi `issues`.
 * @returns le nombre de N° écrits (la page relit alors ses listes).
 */
export async function chercherNumerosManquants(issues: readonly Issue[], maintenant = Date.now()): Promise<number> {
  if (enCours || inaccessibleAujourdhui()) return 0
  const aChercher = enAttenteDeNumero(issues).filter((i) => maintenant - (tentatives.get(i.id) ?? 0) >= INTERVALLE_MS)
  if (aChercher.length === 0) return 0

  enCours = true
  for (const i of aChercher) tentatives.set(i.id, maintenant)
  let ecrits = 0
  try {
    const [{ lignes }, deposees] = await Promise.all([lignesRegistreNcs(), declarationsDeposees()])
    const prises = new Set(issues.flatMap((i) => (i.refNcs ? [i.refNcs] : [])))
    // La plus récente déclaration de chaque problème.
    const parProbleme = new Map<string, DeclarationDeposee>()
    for (const d of deposees) {
      const connue = parProbleme.get(d.issueId)
      if (!connue || d.deposeeLe > connue.deposeeLe) parProbleme.set(d.issueId, d)
    }

    for (const issue of aChercher) {
      const depot = parProbleme.get(issue.id)
      if (!depot) continue
      try {
        const numero = await avecVerrou(issue.id, async () => {
          const envoyee = await declarationEnvoyee(depot.fichier, issue.id, jourDe(depot.deposeeLe), depot.auteur)
          const resultat = chercherDansRegistre(envoyee, lignes, prises)
          if (resultat.etat !== 'trouvee') return null
          const trouve = await getIssueById(issue.id)
          if (!trouve) return null
          const { ecrit } = await enregistrerReferenceNcs(trouve.itemId, resultat.candidat.ligne.numero)
          if (!ecrit) return null
          // Best-effort, comme les autres événements : le N° est déjà écrit.
          await addCommentaire({
            problemId: issue.id,
            type: 'Modification',
            contenu: `N° de NC-S trouvé dans le registre qualité : ${resultat.candidat.ligne.numero}`,
          }).catch((err: unknown) => console.warn('[chercheur NC-S] événement non tracé :', err))
          return resultat.candidat.ligne.numero
        })
        if (numero) {
          prises.add(numero)
          ecrits++
        }
      } catch (err) {
        console.warn(`[chercheur NC-S] ${issue.id} :`, err)
      }
    }
  } catch (err) {
    if (err instanceof RegistreInaccessible) noterInaccessible()
    else console.warn('[chercheur NC-S] recherche de fond :', err)
  } finally {
    enCours = false
  }
  return ecrits
}

/** Bancs d'essai : repartir de zéro. */
export function oublierTentatives(): void {
  tentatives.clear()
  enCours = false
}

// ─── Le suivi des NC-S par la qualité (06/10) ───

let dernierSuivi = { cle: '', t: 0 }

/**
 * Range l'état des NC-S des problèmes chargés (celles qui ont un N°) dans
 * l'instantané partagé, lu par tous (utils/suiviNcs). Même registre que la
 * recherche du N° (déjà en mémoire, retéléchargé seulement s'il a changé).
 * Pas plus d'une fois toutes les 10 minutes pour les mêmes NC-S.
 * @returns le bloc écrit, ou null.
 */
export async function actualiserSuiviNcs(issues: readonly Issue[], maintenant = Date.now()): Promise<BlocSuiviNcs | null> {
  const numeros = new Set(issues.flatMap((i) => (i.refNcs && i.statut !== 'Annulé' ? [i.refNcs] : [])))
  if (numeros.size === 0 || inaccessibleAujourdhui()) return null
  const cle = [...numeros].sort().join('|')
  if (cle === dernierSuivi.cle && maintenant - dernierSuivi.t < INTERVALLE_MS) return null
  dernierSuivi = { cle, t: maintenant }
  try {
    const { lignes } = await lignesRegistreNcs()
    return await enregistrerSuiviNcs(lignes, numeros, new Date(maintenant))
  } catch (err) {
    if (err instanceof RegistreInaccessible) noterInaccessible()
    else console.warn('[chercheur NC-S] suivi qualité :', err)
    return null
  }
}

/** Bancs d'essai : repartir de zéro. */
export function oublierSuivi(): void {
  dernierSuivi = { cle: '', t: 0 }
}
